/**
 * Workflow run detail drawer showing per-node metrics, cache outcomes, and context usage.
 *
 * @module pages/WorkflowRunDetail
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardContent,
  DataTable,
  LoadingOrbit,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowSquareOut, Play } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { BenchmarkNodeMetrics } from '@chatsdet/types';
import {
  useWorkflowRunDetailQuery,
  workflowRunStatusVariants,
  workflowRunStatusLabels,
} from '../useBenchmarkData';
import { formatDuration, formatTokenCount } from './useWorkflowSuiteTransforms';

const nodeStatusVariants: Record<string, 'success' | 'destructive' | 'secondary' | 'warning'> = {
  success: 'success',
  failed: 'destructive',
  skipped: 'secondary',
  running: 'warning',
  pending: 'secondary',
};

const nodeColumns: ColumnDef<StockFeatures, BenchmarkNodeMetrics>[] = [
  {
    accessorKey: 'nodeName',
    header: 'Node',
    cell: ({ row }) => (
      <Text weight="medium" className="truncate max-w-[250px]" title={row.original.nodeName}>
        {row.original.nodeName}
      </Text>
    ),
  },
  {
    accessorKey: 'nodeType',
    header: 'Type',
    cell: ({ row }) => <TagBadge name={row.original.nodeType} color="gray" hideDot />,
  },
  {
    accessorKey: 'durationMs',
    header: 'Duration',
    cell: ({ row }) => <Text>{formatDuration(row.original.durationMs)}</Text>,
  },
  {
    accessorKey: 'inputTokens',
    header: 'Input Tokens',
    cell: ({ row }) => <Text>{formatTokenCount(row.original.inputTokens)}</Text>,
  },
  {
    accessorKey: 'outputTokens',
    header: 'Output Tokens',
    cell: ({ row }) => <Text>{formatTokenCount(row.original.outputTokens)}</Text>,
  },
  {
    accessorKey: 'totalTokens',
    header: 'Total Tokens',
    cell: ({ row }) => <Text weight="medium">{formatTokenCount(row.original.totalTokens)}</Text>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={row.original.status}
        color={badgeVariantToTagColor(nodeStatusVariants[row.original.status] ?? 'secondary')}
        hideDot
      />
    ),
  },
  {
    id: 'cache',
    header: 'Cache',
    cell: ({ row }) => {
      const { cacheOutcome, cacheHitSource } = row.original;
      if (!cacheOutcome) return <Text className="text-muted-foreground">{'\u2014'}</Text>;
      if (cacheOutcome === 'hit') {
        if (cacheHitSource === 'recovery')
          return <TagBadge name="L2 Recovery" color="yellow" hideDot />;
        if (cacheHitSource === 'l2_gap') return <TagBadge name="L2 Gap" color="yellow" hideDot />;
        if (cacheHitSource === 'wait_shortcut')
          return <TagBadge name="Wait Shortcut" color="blue" hideDot />;
        return <TagBadge name="Cache Hit" color="blue" hideDot />;
      }
      if (cacheOutcome === 'fallback') return <TagBadge name="Ejected" color="red" hideDot />;
      if (cacheOutcome === 'miss') return <TagBadge name="Miss" color="gray" hideDot />;
      if (cacheOutcome === 'skipped') return <TagBadge name="Skipped" color="gray" hideDot />;
      return <TagBadge name={cacheOutcome} color="gray" hideDot />;
    },
  },
  {
    id: 'peakContext',
    header: 'Peak Context',
    cell: ({ row }) => {
      const { maxInputTokens, contextWindowTokens } = row.original;
      if (!maxInputTokens || !contextWindowTokens)
        return <Text className="text-muted-foreground">{'\u2014'}</Text>;
      const pct = Math.round((maxInputTokens / contextWindowTokens) * 100);
      const variant = pct > 80 ? 'text-destructive' : pct > 60 ? 'text-warning' : 'text-success';
      return (
        <Text size="sm" className={variant}>
          {pct}%
        </Text>
      );
    },
  },
];

interface WorkflowRunDetailProps {
  runId: string;
  onBack: () => void;
}

export function WorkflowRunDetail({ runId, onBack }: WorkflowRunDetailProps) {
  const query = useWorkflowRunDetailQuery(runId);

  if (query.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-10">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  const run = query.data?.workflowRun;
  if (!run) {
    return <Text className="text-muted-foreground">Workflow run not found.</Text>;
  }

  return (
    <Box className="space-y-4">
      {/* Header */}
      <Flex align="center" gap="md">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <Heading level={4}>{run.workflowName}</Heading>
          <Flex gap="sm" align="center" className="mt-1">
            <TagBadge
              name={workflowRunStatusLabels[run.status]}
              color={badgeVariantToTagColor(workflowRunStatusVariants[run.status])}
              hideDot
            />
            <Text size="sm" className="text-muted-foreground">
              Run #{run.runNumber}
            </Text>
            <TagBadge
              name={run.isCached ? 'Cached' : 'Uncached'}
              color={badgeVariantToTagColor(run.isCached ? 'success' : 'secondary')}
              hideDot
            />
          </Flex>
        </div>
        <Flex gap="sm" align="center">
          <Link
            to="/flows/$workflowId"
            params={{ workflowId: run.sourceWorkflowId }}
            className="flex items-center gap-1 text-sm text-muted-foreground no-underline hover:text-foreground transition-colors"
          >
            <ArrowSquareOut size={14} />
            View Flow
          </Link>
          {run.workflowRunId && (
            <Link
              to="/flows/$workflowId"
              params={{ workflowId: run.copyWorkflowId }}
              search={{ runId: run.workflowRunId }}
              className="flex items-center gap-1 text-sm text-muted-foreground no-underline hover:text-foreground transition-colors"
            >
              <Play size={14} />
              View Run
            </Link>
          )}
        </Flex>
      </Flex>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Duration
            </Text>
            <Text size="lg" weight="bold">
              {formatDuration(run.durationMs)}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Total Tokens
            </Text>
            <Text size="lg" weight="bold">
              {formatTokenCount(run.totalTokens)}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Nodes
            </Text>
            <Text size="lg" weight="bold">
              {run.successfulNodes ?? 0}/{run.totalNodes ?? 0}
              <Text as="span" size="sm" className="text-muted-foreground ml-1">
                passed
              </Text>
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              I/O Tokens
            </Text>
            <Text size="lg" weight="bold">
              {formatTokenCount(run.inputTokens)}/{formatTokenCount(run.outputTokens)}
            </Text>
          </CardContent>
        </Card>
      </div>

      {/* Error message */}
      {run.errorMessage && (
        <Card padding="sm" className="border-destructive/30 bg-destructive/5">
          <CardContent>
            <Text size="sm" weight="medium" className="text-destructive mb-1">
              Error
            </Text>
            <Text size="sm" className="text-muted-foreground whitespace-pre-wrap">
              {run.errorMessage}
            </Text>
          </CardContent>
        </Card>
      )}

      {/* Node metrics table */}
      {run.nodeMetrics && run.nodeMetrics.length > 0 ? (
        <Card padding="md">
          <CardContent>
            <Heading level={5} className="mb-3">
              Node Metrics
            </Heading>
            <DataTable columns={nodeColumns} data={run.nodeMetrics} />
          </CardContent>
        </Card>
      ) : (
        <Text className="text-muted-foreground">No node-level metrics available.</Text>
      )}
    </Box>
  );
}
